import { useState } from 'react';
import { Alert } from 'antd';
import {
  FileTextOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { usePermissions } from '../../hooks/shared/usePermissions';
import { formatReleaseDate } from '../../models/Release/releaseVersions';
import { releaseApi, type ManualKind } from '../../services/releaseApi';
import { ReleaseHistoryModal, RELEASE_ERROR } from './ReleaseHistoryModal';
import { useReleaseHistory } from './useReleaseHistory';
import styles from './style.module.css';

const DOWNLOAD_ERROR = 'Не удалось скачать файл. Пожалуйста, попробуйте позже';

export const HomeReleaseFooter = () => {
  const { canDownloadAdminManual } = usePermissions();
  const { versions, loading, failed, reload } = useReleaseHistory();
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState<ManualKind | null>(null);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const latest = versions[0];

  const download = async (kind: ManualKind) => {
    setDownloading(kind);
    setDownloadFailed(false);
    try {
      await releaseApi.downloadManual(kind);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(null);
    }
  };

  const manualButton = (kind: ManualKind, label: string) => (
    <button
      type="button"
      className={styles.link}
      disabled={downloading !== null}
      aria-busy={downloading === kind}
      onClick={() => void download(kind)}
    >
      {downloading === kind ? <LoadingOutlined /> : <FileTextOutlined />}
      <span>{label}</span>
    </button>
  );

  return (
    <footer className={styles.footer}>
      <div className={styles.items}>
        <div className={styles.group}>
          <button
            type="button"
            className={styles.link}
            disabled={loading}
            aria-haspopup="dialog"
            onClick={() => {
              setOpen(true);
              void reload();
            }}
          >
            <InfoCircleOutlined />
            <span>
              {loading && !latest
                ? 'Загрузка версии…'
                : failed
                  ? 'Информация о релизах'
                  : latest
                    ? `Версия ${latest.version}${latest.date ? ` от ${formatReleaseDate(latest.date)}` : ''}`
                    : 'История релизов'}
            </span>
          </button>
        </div>
        <div className={styles.group} aria-label="Документация">
          {canDownloadAdminManual &&
            manualButton('admin', 'Руководство администратора')}
          {manualButton('user', 'Руководство пользователя')}
        </div>
        <div className={`${styles.group} ${styles.support}`}>
          <MailOutlined aria-hidden="true" />
          <span>Техническая поддержка:</span>
          <a href="mailto:support@s2snext.com">support@s2snext.com</a>
        </div>
      </div>
      {downloadFailed && (
        <Alert type="error" showIcon message={DOWNLOAD_ERROR} />
      )}
      {failed && !open && (
        <Alert type="error" showIcon message={RELEASE_ERROR} />
      )}
      <ReleaseHistoryModal
        open={open}
        onClose={() => setOpen(false)}
        loading={loading}
        failed={failed}
        versions={versions}
      />
    </footer>
  );
};
